import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Lightbox from "react-18-image-lightbox";
import "react-18-image-lightbox/style.css";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import DownloadButton from "./Download/DownloadButton";
import handleDownloadClick from "./Download/onClickDownloadVideo";
// import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "./Download/TooltipStyles.css";
import axios from "axios";
import handleReArchive from "./services/GetFilesReArchived";
import fetchVideoSASToken from "./services/GetVideoSasToken";
import fetchNonVideoSASToken from "./services/GetNonVideoSasToken";
import fetchVideoIndexerTokenAPI from "./services/GetVideoIndexerToken";
import ErrorDisplay from "./utilities/ErrorDisplay";
import archiveIcon from "../icons/archive.png";
import failedToLoad from "../icons/failedToLoad.jpeg";
import {
  CardBuilder,
  CardDownloadIcon,
  Metadata,
} from "./utilities/CardBuilder";
import DownloadSidebar from "./Download/DownloadSidebar";

const fileTypeOptions = [
  { key: "images", label: "Images", extensions: ["png", "jpg", "jpeg", "heif", "heic", "gif", "bmp", "webp"] },
  { key: "videos", label: "Videos", extensions: ["mp4", "avi", "hevc", "wmv", "mov", "mkv", "webm"] },
  { key: "documents", label: "Documents", extensions: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "csv"] },
  { key: "others", label: "Others", extensions: [] },
];

const CCTV_Gallery = ({
  searchApiUrl,
  videoindexerAccountId,
  videoindexerLocation,
  accessToken,
  workNo,
  woliNo,
  saNo,
}) => {
  const [users, setUsers] = useState([]);
  const [wo, setWo] = useState([]);
  const [nonVideoSasToken, setNonVideoSasToken] = useState("");
  const [videoSasToken, setVideoSasToken] = useState("");
  const [videoIndexerToken, setVideoIndexerToken] = useState("");
  
  //Lightbox & LazyLoading states
  const [isCaptionVisible, setIsCaptionVisible] = useState(false);
  const [blobUrls, setBlobUrls] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);  
  const [serviceAppointment, setServiceAppointment] = useState([]);
  const [lat, setLat] = useState([]);
  const [long, setLong] = useState([]);
  const [capturedDate, setCapturedDate] = useState([]);
  const [fileUploadedDateTime, setfileUploadedDateTime] = useState([]);
  const [dateTime, setDateTime] = useState([]);
  const [uploadedBy, setUploadedBy] = useState([]);
  const [organization, setOrganization] = useState([]);
  const [ocrData, setOcrData] = useState([]);
  const [fileDesc, setFiledesc] = useState([]);
  const [isOpenImage, setIsOpenImage] = useState(false);
  const [imageError, setImageError] = useState({});
  const [isSingleDateMode, setIsSingleDateMode] = useState(false);


  //Error handling states
  const [unavailable, setUnavailable] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  // const [downloaded, setIsDownloaded] = useState(false);

  // Re-archiving states
  const [listThumbnails, setListThumbnails] = useState([]);
  const [archivedImages, setArchivedImages] = useState([]);

  // Download states
  const [downloadImages, setDownloadImages] = useState([]);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [isDownloadSidebarVisible, setIsDownloadSidebarVisible] = useState(false);
  const [isDownloadSidebarCollapsed, setIsDownloadSidebarCollapsed] =
    useState(false);
  const [isGalleryControlsOpen, setIsGalleryControlsOpen] = useState(false);
  const [galleryControlsTab, setGalleryControlsTab] = useState("filter");
  const [sortMode, setSortMode] = useState("captured-desc");
  const [selectedFileTypes, setSelectedFileTypes] = useState({
    images: true,
    videos: true,
    documents: true,
    others: true,
  });
  const [excludedEngineers, setExcludedEngineers] = useState([]);
  const [engineerSearch, setEngineerSearch] = useState("");
  const [capturedDateFrom, setCapturedDateFrom] = useState("");
  const [capturedDateTo, setCapturedDateTo] = useState("");
  const [includeNoCapturedDate, setIncludeNoCapturedDate] = useState(true);

  const [isCheckAll, setIsCheckAll] = useState(false);
  const [isCheck, setIsCheck] = useState([]);
  const [isRefreshingTokens, setIsRefreshingTokens] = useState(false);
  const [expandedVideoId, setExpandedVideoId] = useState(null);
  const [videoIndexStatuses, setVideoIndexStatuses] = useState({});
  const videoIndexStatusesRef = useRef({});
  const authDialogShownRef = useRef(false);
  const extendingSessionRef = useRef(false);
  const accessTokenDialogShownRef = useRef(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(1);
  const [collapsedWolis, setCollapsedWolis] = useState({});
  const [collapsedSas, setCollapsedSas] = useState({});
  // Calculate the indexes of items to display on the current page
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const visibleUsers = useMemo(() => {
    const activeFileTypes = Object.entries(selectedFileTypes)
      .filter(([, isEnabled]) => isEnabled)
      .map(([key]) => key);

    const normalizedEngineerSearch = engineerSearch.trim().toLowerCase();
    const excludedEngineerSet = new Set(excludedEngineers);

    const resolveFileType = (fileExtension = "") => {
      const normalizedExt = String(fileExtension).toLowerCase();
      const matchedType = fileTypeOptions.find(
        (option) =>
          option.key !== "others" && option.extensions.includes(normalizedExt),
      );
      return matchedType ? matchedType.key : "others";
    };

    const parseGalleryDate = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const getCapturedDateValue = (record) =>
      record?.dateOnImage || "";

    const sortGalleryRecords = (records) => {
      const safeRecords = [...records];
      const getDateScore = (record, field) => {
        const parsed = parseGalleryDate(field(record));
        return parsed ? parsed.getTime() : Number.NEGATIVE_INFINITY;
      };

      return safeRecords.sort((left, right) => {
        switch (sortMode) {
          case "captured-asc":
            {
              const leftDate = getCapturedDateValue(left);
              const rightDate = getCapturedDateValue(right);
              const leftHasDate = Boolean(leftDate);
              const rightHasDate = Boolean(rightDate);

              if (leftHasDate !== rightHasDate) {
                return leftHasDate ? -1 : 1;
              }

              if (!leftHasDate && !rightHasDate) {
                return String(left.fileName || "").localeCompare(
                  String(right.fileName || ""),
                );
              }

              return (
                getDateScore(left, getCapturedDateValue) -
                getDateScore(right, getCapturedDateValue) ||
                String(left.fileName || "").localeCompare(
                  String(right.fileName || ""),
                )
              );
            }
          case "uploaded-desc":
            return (
              getDateScore(right, (record) => record?.fileUploadedDateTime || "") -
              getDateScore(left, (record) => record?.fileUploadedDateTime || "") ||
              String(left.fileName || "").localeCompare(
                String(right.fileName || ""),
              )
            );
          case "name-asc":
            return String(left.fileName || "").localeCompare(
              String(right.fileName || ""),
            );
        case "name-desc":
          return String(right.fileName || "").localeCompare(
            String(left.fileName || ""),
          );
        case "engineer-asc":
          return (
            String(left.engineerName || "").localeCompare(
              String(right.engineerName || ""),
            ) ||
            String(left.fileName || "").localeCompare(String(right.fileName || ""))
          );
        case "captured-desc":
        default:
          return (
            getDateScore(right, getCapturedDateValue) -
            getDateScore(left, getCapturedDateValue) ||
              String(left.fileName || "").localeCompare(
                String(right.fileName || ""),
              )
            );
        }
      });
    };

const matchesDateRange = (record) => {
  const recordDate = parseGalleryDate(getCapturedDateValue(record));

  if (!recordDate) {
    return includeNoCapturedDate;
  }

  // Single Date Mode
  if (isSingleDateMode && capturedDateFrom) {
    const selectedDate = parseGalleryDate(`${capturedDateFrom}T00:00:00`);

    if (!selectedDate) return true;

    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    return recordDate >= startOfDay && recordDate <= endOfDay;
  }

  // Date Range Mode
  if (capturedDateFrom) {
    const from = parseGalleryDate(`${capturedDateFrom}T00:00:00`);
    if (from && recordDate < from) {
      return false;
    }
  }

  if (capturedDateTo) {
    const to = parseGalleryDate(`${capturedDateTo}T23:59:59.999`);
    if (to && recordDate > to) {
      return false;
    }
  }

  return true;
};

    return users
      .map((user) => {
        const filteredWolis = (user.data || [])
          .map((woli) => {
            const filteredRecords = (woli.data || []).filter((record) => {
              const fileType = resolveFileType(record?.fileExtension);
              const engineerName = String(record?.engineerName || "Unknown");
              const engineerNameNormalized = engineerName.toLowerCase();
              const fileTypeMatches = activeFileTypes.includes(fileType);
              const engineerMatches = !excludedEngineerSet.has(engineerName);
              const searchMatches =
                normalizedEngineerSearch.length === 0 ||
                engineerNameNormalized.includes(normalizedEngineerSearch);

              return (
                fileTypeMatches &&
                engineerMatches &&
                searchMatches &&
                matchesDateRange(record)
              );
            });

            if (filteredRecords.length === 0) return null;

            return {
              ...woli,
              data: sortGalleryRecords(filteredRecords),
            };
          })
          .filter(Boolean);

        if (filteredWolis.length === 0) return null;

        return {
          ...user,
          data: filteredWolis,
        };
      })
      .filter(Boolean);
  }, [
    users,
    capturedDateFrom,
    capturedDateTo,
    engineerSearch,
    includeNoCapturedDate,
    excludedEngineers,
    selectedFileTypes,
    sortMode,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [visibleUsers]);

  const engineerOptions = useMemo(() => {
    const names = new Set();

    users.forEach((user) => {
      user.data?.forEach((woli) => {
        woli.data?.forEach((record) => {
          names.add(String(record?.engineerName || "Unknown"));
        });
      });
    });

    return Array.from(names).sort((left, right) =>
      left.localeCompare(right),
    );
  }, [users]);

 useEffect(() => {
    getSelectedUsers();
    fetchVideoSasToken();
    fetchNonVideoSasToken();
    fetchVideoIndexerToken();
  }, []);

  const fetchVideoSasToken = async () => {
    try {
      setVideoSasToken(""); // Clear before fetching to signal refresh
      const token = await fetchVideoSASToken(accessToken);
      setVideoSasToken(token);
    } catch (err) {
      console.error("Failed to fetch video SAS token:", err);
      setVideoSasToken("");
      throw err;
    }
  };
  const fetchNonVideoSasToken = async () => {
    try {
      setNonVideoSasToken(""); // Clear before fetching to signal refresh
      const token = await fetchNonVideoSASToken(accessToken);
      setNonVideoSasToken(token);
    } catch (err) {
      console.error("Failed to fetch non-video SAS token:", err);
      setNonVideoSasToken("");
      throw err;
    }
  };
  const fetchVideoIndexerToken = async () => {
    try {
      setVideoIndexerToken(""); // Clear before fetching to signal refresh
      const token = await fetchVideoIndexerTokenAPI();
      setVideoIndexerToken(token);
    } catch (err) {
      console.error("Failed to fetch video indexer token:", err);
      setVideoIndexerToken("");
      throw err;
    }
  };

  const appendVideoSasTokenToUrl = (url) => {
    if (!url || !videoSasToken) return url;
    // const separator = url.includes("?") ? "&" : "?";
    return `${url}${videoSasToken}`;
  };

  const appendNonVideoSasTokenToUrl = (url) => {
    if (!url || !nonVideoSasToken) return url;
    // const separator = url.includes("?") ? "&" : "?";
    return `${url}${nonVideoSasToken}`;
  };

  const buildVideoIndexerThumbnailUrl = (videoStreamingId, videoThumbnailId) => {
    const accountId = videoindexerAccountId;
    const location = videoindexerLocation || "westeurope";
    if (!videoStreamingId || !videoThumbnailId || !accountId || !location || !videoIndexerToken) {
      return null;
    }
    return `https://api.videoindexer.ai/${location}/Accounts/${accountId}/Videos/${videoStreamingId}/Thumbnails/${videoThumbnailId}?accessToken=${videoIndexerToken.accessToken}`;
  };

  const buildVideoIndexerIndexUrl = useCallback((videoStreamingId) => {
    const accountId = videoindexerAccountId;
    const location = videoindexerLocation || "westeurope";
    if (!videoStreamingId || !accountId || !location || !videoIndexerToken) {
      return null;
    }
    return `https://api.videoindexer.ai/${location}/Accounts/${accountId}/Videos/${videoStreamingId}/Index?accessToken=${videoIndexerToken.accessToken}`;
  }, [videoIndexerToken]);

  const fetchVideoIndexerStatus = useCallback(async (videoStreamingId) => {
    const url = buildVideoIndexerIndexUrl(videoStreamingId);
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(`Failed to fetch video index status for ${videoStreamingId}:`, response.status);
        return;
      }
      const data = await response.json();
      const state = data.state || data?.videos?.[0]?.state || "Uploaded";
      const progress = data.processingProgress || data?.videos?.[0]?.processingProgress || "";
      setVideoIndexStatuses((prev) => {
        const next = {
          ...prev,
          [videoStreamingId]: {
            state,
            progress,
          },
        };
        videoIndexStatusesRef.current = next;
        return next;
      });
    } catch (err) {
      console.error(`Error fetching video index status for ${videoStreamingId}:`, err);
    }
  }, [buildVideoIndexerIndexUrl]);

  useEffect(() => {
    if (!videoIndexerToken) return;

    const videoIds = Array.from(
      new Set(
        users.flatMap((item) =>
          item.data.flatMap((innerItem) =>
            innerItem.data
              .map((file) => file.videoStreamingId)
              .filter(Boolean),
          ),
        ),
      ),
    );

    videoIds.forEach((videoStreamingId) => {
      const status = videoIndexStatusesRef.current[videoStreamingId];
      if (!status || status.state !== "Processed") {
        fetchVideoIndexerStatus(videoStreamingId);
      }
    });
  }, [videoIndexerToken, users, fetchVideoIndexerStatus]);

  const handleAuthFailure = () => {
    if (authDialogShownRef.current || isRefreshingTokens) return;
    authDialogShownRef.current = true;

    createDialogBox(
      "Session expired",
      "Your session has expired due to inactivity.",
      [
        {
          type: "button",
          text: "Extend session",
          attributes: { id: "dialog-box-extend-button" },
          listener: {
            event: "click",
            callback: async () => {
              if (extendingSessionRef.current) return;
              extendingSessionRef.current = true;
              setIsRefreshingTokens(true);

              if (closeDialogBox) closeDialogBox();

              try {
                // Clear all failed downloads before refreshing tokens
                setDownloadProgress((prev) => {
                  const updated = { ...prev };
                  Object.keys(updated).forEach((key) => {
                    if (updated[key]?.status === "failed") {
                      delete updated[key];
                    }
                  });
                  return updated;
                });

                await fetchVideoSasToken();
                await fetchNonVideoSasToken();
              } catch (err) {
                console.error("Failed to extend session:", err);
              } finally {
                authDialogShownRef.current = false;
                extendingSessionRef.current = false;
                setIsRefreshingTokens(false);
              }
            },
          },
        },
      ],
      () => {
        authDialogShownRef.current = false;
        extendingSessionRef.current = false;
        setIsRefreshingTokens(false);
        if (closeDialogBox) closeDialogBox();
        setErrorMessage("You currently do not have access to this page.\n Please try again.");
      },
    );
  };

  // Show a dialog telling the user to reload the page from Salesforce
  // and reload the page when the dialog is closed.
  const showAccessTokenExpiredDialog = () => {
    if (accessTokenDialogShownRef.current) return;
    accessTokenDialogShownRef.current = true;

    if (typeof createDialogBox === "function") {
      createDialogBox(
        "Session expired",
        "Please load the page again from salesforce",
        [],
        () => {
          try {
            if (typeof closeDialogBox === "function") closeDialogBox();
          } catch (e) {
            // ignore
          } finally {
            accessTokenDialogShownRef.current = false;
          }
          window.location.reload();
        },
      );
    } else {
      accessTokenDialogShownRef.current = false;
      window.location.reload();
    }
  };

  const checkUrlValidity = async (url) => {
    try {
      const resp = await fetch(url, { method: "HEAD", mode: "cors" });
      if (!resp.ok && (resp.status === 403)) {
        handleAuthFailure();
        return false;
      }else if (!resp.ok && (resp.status === 401)) {
        showAccessTokenExpiredDialog();
        return false;
      }
    } catch {
      // network error; ignore for validity
    }
    return true;
  };

  const downloadWithAuthCheck = async (...args) => {
    await handleDownloadClick(...args);
  };

  const handleCaptionToggle = () => {
    setIsCaptionVisible(!isCaptionVisible);
  };

  const handleImageError = (index) => {
    setImageError((prevErrors) => ({ ...prevErrors, [index]: true }));
  };

  const handleImageLoad = (index) => {
    setImageError((prevErrors) => {
      if (!prevErrors[index]) return prevErrors;
      const nextErrors = { ...prevErrors };
      delete nextErrors[index];
      return nextErrors;
    });
  };

  useEffect(() => {
    // After SAS token refresh, allow all thumbnails to retry with the new token.
    setImageError({});
  }, [videoSasToken, nonVideoSasToken]);

  const handleMovePrevRequest = async () => {
    const newIndex = (photoIndex + blobUrls.length - 1) % blobUrls.length;
    const url = blobUrls[newIndex];
    const ok = await checkUrlValidity(url);
    if (!ok) return;
    setPhotoIndex(newIndex);
  };

  const handleMoveNextRequest = async () => {
    const newIndex = (photoIndex + 1) % blobUrls.length;
    const url = blobUrls[newIndex];
    const ok = await checkUrlValidity(url);
    if (!ok) return;
    setPhotoIndex(newIndex);
  };

  // const downloadButton = [
  //   <button
  //     className="btn-download"
  //     disabled={!isCheckAll && downloadImages.length === 0}
  //     onClick={() => {
  //       if (isCheckAll) {
  //         ImgUri.forEach(function (e) {
  //           var fileurl = e.split("$")[0];
  //           var downloadFileName = e.split("$")[1];
  //           var downloadFileNameExtension = e.split("$")[2];
  //           fetch(fileurl)
  //             .then((response) => response.blob())
  //             .then((blob) => {
  //               var a = document.createElement("a");
  //               var url = URL.createObjectURL(blob);
  //               a.href = url;
  //               if (downloadFileName.includes(".")) {
  //                 a.download = downloadFileName;
  //               } else {
  //                 a.download =
  //                   downloadFileName + "." + downloadFileNameExtension;
  //               }
  //               a.click();
  //               URL.revokeObjectURL(url);
  //             })
  //             .catch((error) =>
  //               console.error("Error downloading attachment:", error),
  //             );
  //         });
  //       } else {
  //         downloadImages.forEach(function (e) {
  //           var fileurl = e.image.split("$")[0];
  //           var downloadFileName = e.image.split("$")[1];
  //           var downloadFileNameExtension = e.image.split("$")[2];
  //           fetch(fileurl)
  //             .then((response) => response.blob())
  //             .then((blob) => {
  //               var a = document.createElement("a");
  //               var url = URL.createObjectURL(blob);
  //               a.href = url;
  //               if (downloadFileName.includes(".")) {
  //                 a.download = downloadFileName;
  //               } else {
  //                 a.download =
  //                   downloadFileName + "." + downloadFileNameExtension;
  //               }
  //               a.click();
  //               URL.revokeObjectURL(url);
  //             })
  //             .catch((error) =>
  //               console.error("Error downloading attachment:", error),
  //             );
  //         });
  //         setIsDownloaded(true);
  //       }
  //     }}
  //   >
  //     Download
  //   </button>,
  // ];

  const showHideMetaDataButton = [
    <button
      key="captionToggle"
      className="toggleImageCaptionButton"
      onClick={handleCaptionToggle}
    >
      {isCaptionVisible ? "Hide metadata" : "Show metadata"}
    </button>,
  ];

  // we no longer need external mutable arrays; selections are derived from the
  // current `users` state each render.

  const workdIds = wo.map((woId) => {
    return woId.workOrderNumber;
  });

  const onClose = () => {
    window.opener = null;
    window.open("", "_self");
    window.close();
  };

  //#region Loader
  const loaderOverlay = document.createElement("div");
  loaderOverlay.id = "loader-overlay";
  const loader = document.createElement("div");
  loader.id = "loader";
  document.body.appendChild(loaderOverlay);
  document.body.appendChild(loader);
  const loaderdiv = document.getElementById("loader");
  const loaderOverlaydiv = document.getElementById("loader-overlay");
  //#endregion

  const getSelectedUsers = async () => {
    let searchUrl = `${searchApiUrl}GetCCTVDetails?`;
    
    if (workNo != null) searchUrl += `WorkOrderNumber=${workNo}&`;
    if (woliNo != null) searchUrl += `WorkOrderLineItemNumber=${woliNo}&`;
    if (saNo != null) searchUrl += `ServiceAppointmentNumber=${saNo}&`;
    
    // Remove the trailing '&'
    searchUrl = searchUrl.substring(0, searchUrl.length - 1);
    console.log("Search api url with parameters :- " + searchUrl);
    loaderdiv.style.visibility = "visible";
    loaderOverlaydiv.style.display = "flex";
    const response = await fetch(searchUrl, {
      method: "get",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });
    if (response.ok) {
      loaderdiv.style.visibility = "hidden";
      loaderOverlaydiv.style.display = "none";
      var Users = await response.json();
     function groupBy(arr, prop) {
        return Object.values(
          arr.reduce((aggregate, item) => {
            const val = item[prop];
            if (!aggregate[val]) {
              aggregate[val] = {
                [prop]: val,
                data: [],
              };
            }
            aggregate[val].data.push(item);
            return aggregate;
          }, {}),
        );
      }
    if(Users.length === 0)
          {
            setUnavailable(true);
    }
    if (Users.length > 0) 
          {
              const grouped = groupBy(Users, "workOrderLineItemNumber").map(
                (item) => ({
                  ...item,
                  data: groupBy(item.data, "serviceAppointmentNumber"),
                }),
              );
              // console.log("grouped",grouped);
              setUsers(grouped);
              var WO = Users;
              setWo(WO);
    } 
    }
    else if (response.status === 400) {
          loaderdiv.style.visibility = "hidden";
          loaderOverlaydiv.style.display = "none";
          setUnavailable(true);
    }
    else if (response.status === 401) {
          loaderdiv.style.visibility = "hidden";
          loaderOverlaydiv.style.display = "none";
          setErrorMessage(
          "You currently do not have access to this page.\n Please try again.",
          );
    }
  };

  const handleAdd = (image, isFileArchived) => {
    const selectedImages = [...downloadImages];
    selectedImages.push({ image, isFileArchived });
    setDownloadImages(selectedImages);
    setArchivedImages(selectedImages);
    // Reset download progress when new items are added
    setDownloadProgress({});
  };

  const handleRemove = (image) => {
    // image strings can include a trailing fileSize field separated by '$'.
    // When comparing we only care about the first three parts (url, name, ext),
    // so strip off anything after the third dollar sign.
    const base = image.split("$").slice(0, 3).join("$");
    const selectedImages = downloadImages.filter((img) => {
      const candidateBase = (img.image || "").split("$").slice(0, 3).join("$");
      return candidateBase !== base;
    });
    setDownloadImages(selectedImages);
    setArchivedImages(selectedImages);
  };

  const handleAddThumb = (thumb) => {
    setListThumbnails((prevThumbs) => [...prevThumbs, thumb]);
  };

  const handleRemoveThumb = (thumb) => {
    setListThumbnails((prevThumbs) => prevThumbs.filter((t) => t !== thumb));
  };


  // Flatten grouped `users` into a simple list of file records for selection/download.
  const flattenedItems = visibleUsers.flatMap((item) =>
    item.data.flatMap((innerItem) => innerItem.data),
  );

  const ImgUri = flattenedItems.map((file) => {
    return (
      (file.finalFileURL || "") +
      "$" +
      (file.fileName || "") +
      "$" +
      (file.fileExtension || "") +
      "$" +
      (file.fileSize || "")
    );
  });

  const SelectAllCheckbox = ({ isChecked, onChange }) => {
    return (
      <label className="selectAllButton">
        <input
          type="checkbox"
          name="selectAll"
          id="selectAll"
          className="selectAllCheckbox"
          onChange={onChange}
          checked={isChecked}
        />

        <span className="selectAllText">
          {isChecked ? "Unselect All" : "Select All"}
        </span>
      </label>
    );
  };

  const setAllFileTypes = (nextValue) => {
    setSelectedFileTypes({
      images: nextValue,
      videos: nextValue,
      documents: nextValue,
      others: nextValue,
    });
  };

  const handleClearGalleryFilters = () => {
    setSortMode("captured-desc");
    setGalleryControlsTab("filter");
    setEngineerSearch("");
    setCapturedDateFrom("");
    setCapturedDateTo("");
    setIncludeNoCapturedDate(true);
    setAllFileTypes(true);
    setExcludedEngineers([]);
    setIsSingleDateMode(false);
  };

  const toggleEngineerSelection = (engineerName) => {
    setExcludedEngineers((prev) =>
      prev.includes(engineerName)
        ? prev.filter((name) => name !== engineerName)
        : [...prev, engineerName],
    );
  };

  const isGalleryControlActive =
    isGalleryControlsOpen || selectedFileTypes.images === false ||
    selectedFileTypes.videos === false ||
    selectedFileTypes.documents === false ||
    selectedFileTypes.others === false ||
    excludedEngineers.length > 0 ||
    engineerSearch !== "" ||
    capturedDateFrom !== "" ||
    capturedDateTo !== "" ||
    includeNoCapturedDate !== true ||
    sortMode !== "captured-desc";

  const toggleWoliCollapse = (woliKey, woli, isCurrentlyCollapsed) => {
    setCollapsedWolis((prev) => {
      const nextIsCollapsed = !isCurrentlyCollapsed;

      // Apply SA behavior only when expanding a WOLI.
      if (isCurrentlyCollapsed) {
        setCollapsedSas((prevSas) => {
          const updated = { ...prevSas };
          const saList = woli?.data || [];

          if (saList.length === 1) {
            const singleSaKey = `${woliKey}-0-${saList[0].serviceAppointmentNumber}`;
            updated[singleSaKey] = false; // auto expand single SA
          } else if (saList.length > 1) {
            saList.forEach((sa, saIndex) => {
              const saKey = `${woliKey}-${saIndex}-${sa.serviceAppointmentNumber}`;
              updated[saKey] = true; // keep all SAs collapsed for manual expand
            });
          }

          return updated;
        });
      }

      return {
        ...prev,
        [woliKey]: nextIsCollapsed,
      };
    });
  };

  const toggleSaCollapse = (saKey, isCurrentlyCollapsed) => {
    setCollapsedSas((prev) => ({
      ...prev,
      [saKey]: !isCurrentlyCollapsed,
    }));
  };

  const handleSelectAll = () => {
    setIsCheckAll((prevIsCheckAll) => {
      const newIsCheckAll = !prevIsCheckAll;

      if (newIsCheckAll) {
        const allIds = flattenedItems.map((file) => String(file.id));

        setIsCheck(allIds);
        const newDownloadImagesFiles = flattenedItems.map((file) => ({
          image:
            (file.finalFileURL || "") +
            "$" +
            (file.fileName || "") +
            "$" +
            (file.fileExtension || "") +
            "$" +
            (file.fileSize || ""),
          isFileArchived: file.isFileArchived,
        }));
        setDownloadImages(newDownloadImagesFiles);
        setDownloadProgress({});
        setArchivedImages(newDownloadImagesFiles);
      } else {
        setIsCheck([]);
        setDownloadImages([]);
        setDownloadProgress({});
        setArchivedImages([]);
      }
      return newIsCheckAll;
    });
  };

  const handleClick = (e) => {
    const { id, checked } = e.target;
    let updatedIsCheck;
    if (checked) {
      updatedIsCheck = [...isCheck, id];
    } else {
      updatedIsCheck = isCheck.filter((item) => item !== id);
    }
    setIsCheck(updatedIsCheck);
    // if user manually toggles items, update the "select all" checkbox state
    if (typeof flattenedItems !== "undefined") {
      setIsCheckAll(updatedIsCheck.length === flattenedItems.length);
    } else {
      setIsCheckAll(false);
    }
  };

  function createDialogBox(title, message, elements, onClose) {
    const dialogOverlay = document.createElement("div");
    dialogOverlay.classList.add("dialog-overlay");
    const dialogBox = document.createElement("div");
    dialogBox.id = "dialog-box";
    const dialogBoxTitle = document.createElement("div");
    dialogBoxTitle.id = "dialog-box-title";
    dialogBoxTitle.innerHTML = title;
    const dialogBoxMessage = document.createElement("div");
    dialogBoxMessage.id = "dialog-box-message";
    dialogBoxMessage.innerHTML = message;
    // Add any additional elements to the message
    elements.forEach((element) => {
      const el = document.createElement(element.type);
      el.textContent = element.text;
      if (element.attributes) {
        Object.keys(element.attributes).forEach((attr) => {
          el.setAttribute(attr, element.attributes[attr]);
        });
      }
      if (element.listener) {
        el.addEventListener(element.listener.event, element.listener.callback);
      }
      dialogBoxMessage.appendChild(el);
    });
    const closeButton = document.createElement("button");
    closeButton.id = "dialog-box-close-button";
    closeButton.innerText = "Close";
    closeButton.addEventListener("click", onClose);
    dialogBox.appendChild(dialogBoxTitle);
    dialogBox.appendChild(dialogBoxMessage);
    dialogBox.appendChild(closeButton);
    document.body.appendChild(dialogOverlay);
    document.body.appendChild(dialogBox);
  }

  const closeDialogBox = () => {
    const dialogBox = document.querySelector("#dialog-box");
    const dialogOverlay = document.querySelector(".dialog-overlay");
    dialogBox.parentNode.removeChild(dialogBox);
    dialogOverlay.parentNode.removeChild(dialogOverlay);
    document.body.classList.remove("dialog-open");
  };


  const createMetadataProps = (user) => ({
    FileNo: user.FileNo,
    dateOnImage: user.dateOnImage,
    dbInsertedDateTime: user.dbInsertedDateTime,
    logOnUserUploadTime: user.logOnUserUploadTime,
    fileExtension: user.fileExtension,
    fileName: user.fileName,
    fileUploadedDateTime: user.fileUploadedDateTime,
    latitude: user.latitude,
    longitude: user.longitude,
    engineerName: user.engineerName,
    address: user.address,
    serviceAppointmentNumber: user.serviceAppointmentNumber,
    workOrderLineItemNumber: user.workOrderLineItemNumber,
    workOrderNumber: user.workOrderNumber,
  });

  const createCardBuilderProps = (user, tokenType = 'nonVideo') => {
    const baseProps = {
      id: user.id,
      finalFileURL: user.finalFileURL,
      fileName: user.fileName,
      fileExtension: user.fileExtension,
      fileSize: user.fileSize,
      isFileArchived: user.isFileArchived,
      thumbNailURL: user.thumbNailURL,
      isCheck: isCheck,
      handleClick: handleClick,
      handleAdd: handleAdd,
      handleAddThumb: handleAddThumb,
      handleRemove: handleRemove,
      handleRemoveThumb: handleRemoveThumb,
    };

    const tokenProp = tokenType === 'video' 
      ? { videoSasToken } 
      : { nonVideoSasToken };

    // const imageOnlyProps = isImageType 
    //   ? { isCheckAll: isCheckAll, handleSelectAll: handleSelectAll } 
    //   : {};

    return { ...baseProps, ...tokenProp};
  };

  const createDownloadHandler = (user, abortSignal = undefined) => (e) =>
    downloadWithAuthCheck(
      e,
      user.fileName,
      user.fileExtension,
      user.finalFileURL,
      setDownloadProgress,
      videoSasToken,
      nonVideoSasToken,
      createDialogBox,
      closeDialogBox,
      abortSignal,
      fetchVideoSasToken,
      fetchNonVideoSasToken,
      showAccessTokenExpiredDialog,
      user.fileSize,
    );

  function renderImageListItems(
    records,
    downloadProgress,
  ) {
    return records.map((user, index) => {
      const {
        id,
        workOrderNumber,
        fileExtension,
        fileSize,
        finalFileURL,
        isFileArchived,
        thumbNailURL,
        videoStreamingId,
        videoThumbnailId,
      } = user;

      // console.log("Rendering file:", {user});
      const listKey = `${id || finalFileURL || ""}-${index}`;
      const ext = (fileExtension || "").toLowerCase();

      const validExtensions = ["png", "jpg", "jpeg", "heif", "heic"];
      if (validExtensions.includes(ext)) {
        return (
          <li key={listKey}>
            <CardBuilder {...createCardBuilderProps(user, 'nonVideo')} />
            <LazyLoadImage
              effect="blur"
              delayTime={100}
              threshold={500}
              src={
                imageError[id]
                  ? isFileArchived === "true"
                    ? archiveIcon // Case 1: error + archived
                    : failedToLoad // Case 2: error only
                  : appendNonVideoSasTokenToUrl(thumbNailURL || finalFileURL) // Case 3 & 4: no error
              }
              alt={workOrderNumber}
              onError={() => handleImageError(id)}
              onLoad={() => handleImageLoad(id)}
              onClick={() => {
                // only include image records when opening the lightbox
                const imageRecords = records.filter((r) => {
                  const rExt = (r.fileExtension || "").toLowerCase();
                  return validExtensions.includes(rExt);
                });

                setOcrData(
                  imageRecords.map((blob) => {
                    return blob.ocrData;
                  }),
                );
                setServiceAppointment(
                  imageRecords.map((blob) => {
                    return blob.serviceAppointmentNumber;
                  }),
                );
                setCapturedDate(
                  imageRecords.map((blob) => {
                    return blob.dateOnImage;
                  }),
                );
                setfileUploadedDateTime(
                  imageRecords.map((blob) => {
                    return blob.fileUploadedDateTime;
                  }),
                );
                setDateTime(
                  imageRecords.map((blob) => {
                    return blob.imageUploadedDateTime;
                  }),
                );
                setLat(
                  imageRecords.map((blob) => {
                    return blob.latitude;
                  }),
                );
                setLong(
                  imageRecords.map((blob) => {
                    return blob.longitude;
                  }),
                );
                setUploadedBy(
                  imageRecords.map((blob) => {
                    return blob.engineerName;
                  }),
                );
                setOrganization(
                  imageRecords.map((blob) => {
                    return blob.ownerOrganization;
                  }),
                );
                const clickedImageIndex = imageRecords.findIndex((img) => {
                  if (img?.id != null && user?.id != null) {
                    return String(img.id) === String(user.id);
                  }
                  return img?.finalFileURL === user?.finalFileURL;
                });
                const safeClickedImageIndex = clickedImageIndex >= 0 ? clickedImageIndex : 0;
                const nextBlobUrls = imageRecords.map((blob) => {
                  return appendNonVideoSasTokenToUrl(blob.finalFileURL);
                });
                setBlobUrls(nextBlobUrls);
                setPhotoIndex(safeClickedImageIndex);
                setIsOpenImage(nextBlobUrls.length > 0);
                setFiledesc(
                  imageRecords.map((blob) => {
                    return blob.fileDescription;
                  }),
                );
              }}
            />

            <div style={{ float: "left" }} className="imgInfo">
              <Metadata {...createMetadataProps(user)} />
            </div>
          </li>
        );
      }
      const videoExtensions = ["mp4", "avi", "hevc", "wmv", "mov"];
      if (videoExtensions.includes(ext)) {
        const accountId = videoindexerAccountId;
        const location = videoindexerLocation || "westeurope";
        const playerUrl = videoStreamingId && videoIndexerToken && accountId && location
          ? `https://www.videoindexer.ai/embed/player/${accountId}/${videoStreamingId}/?accessToken=${videoIndexerToken.accessToken}&locale=en&location=${location}`
          : null;
        const thumbnailUrl = buildVideoIndexerThumbnailUrl(videoStreamingId, videoThumbnailId);
        const status = videoIndexStatuses[videoStreamingId];
        const isProcessing = status && status.state && status.state !== "Processed";
        const statusLabel = isProcessing
          ? `${status.state} ${status.progress || ""}`.trim()
          : null;
        const isPlayerExpanded = expandedVideoId === id;
        
        const handleThumbnailClick = () => {
          if (isProcessing) return;
          setExpandedVideoId(isPlayerExpanded ? null : id);
        };

        return (
          // <Tippy
          //   key={id || finalFileURL}
          //   content={
          //     <>
          //       <strong>Note:</strong> The download time may vary based on your
          //       internet connection speed, specially for larger files. Please
          //       avoid refreshing the page while waiting.
          //     </>
          //   }
          //   placement="bottom"
          //   theme="custom"
          // >
            <li className="docs" key={listKey}>
              <CardBuilder {...createCardBuilderProps(user, 'video')} />
              {isFileArchived === "true" || !playerUrl ? (
                <a
                  className={`buttonVideo ${
                    isFileArchived === "true" ? "archivedDoc" : ""
                  }`}
                  href={finalFileURL}
                  onClick={createDownloadHandler(user, undefined)}
                >
                  <CardDownloadIcon
                    finalFileURL={finalFileURL}
                    isFileArchived={isFileArchived}
                    downloadProgress={downloadProgress}
                  />
                </a>
              ) : (
                <div className="inlineVideo">
                  
                  {isPlayerExpanded ? (
                    <iframe
                      className="videoPlayer"
                      src={playerUrl}
                      frameBorder="0"
                      allowFullScreen
                      title="Video Indexer Player"
                      allow="local-network-access"/>
                  ) : (
                    <div
                      className="videoThumbnailContainer"
                      onClick={handleThumbnailClick}
                      style={{ cursor: isProcessing ? "default" : "pointer" }}
                    >
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt="Video Thumbnail"
                          className="videoThumbnail"
                        />
                      ) : (
                        <div className="videoThumbnailPlaceholder" />
                      )}
                      {isProcessing ? (
                        <div className="videoProcessingOverlay">
                          <div className="videoProcessingText">
                            {statusLabel || "Processing"}
                          </div>
                        </div>
                      ) : (
                        <div className="playButtonOverlay">
                          <svg className="playIcon" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  <a
                    className=""
                    href={finalFileURL}
                    onClick={createDownloadHandler(user, undefined)}
                  >
                    <CardDownloadIcon
                        finalFileURL={finalFileURL}
                        isFileArchived={isFileArchived}
                        downloadProgress={downloadProgress}
                      />
                  </a>
                </div>
              )}

              <div className="docInfo">
                <Metadata {...createMetadataProps(user)} />
              </div>
            </li>
          // </Tippy>
        );
      } else if (
        ext === "doc" ||
        ext === "docx"
      ) {
        return (
          <li className="docs" key={listKey}>
            <CardBuilder {...createCardBuilderProps(user, 'nonVideo')} />
            {/* <a className="buttonDoc" href={finalFileURL} target="_blank"></a> */}
            <a
              className={`buttonDoc ${
                isFileArchived === "true" ? "archivedDoc" : ""
              }`}
              href={finalFileURL}
              onClick={createDownloadHandler(user, undefined)}
            >
              <CardDownloadIcon
                finalFileURL={finalFileURL}
                isFileArchived={isFileArchived}
                downloadProgress={downloadProgress}
              />
            </a>

            <div className="docInfo">
              <Metadata {...createMetadataProps(user)} />
            </div>
          </li>
        );
      } else if (
        ext === "xls" ||
        ext === "xlsx" ||
        ext === "csv"
      ) {
        return (
          <li className="docs" key={listKey}>
            <CardBuilder {...createCardBuilderProps(user, 'nonVideo')} />
            {/* <a className="buttonXls" href={finalFileURL} target="_blank"></a> */}
            <a
              className={`buttonXls ${
                isFileArchived === "true" ? "archivedDoc" : ""
              }`}
              href={finalFileURL}
              onClick={createDownloadHandler(user, undefined)}
            >
              <CardDownloadIcon
                finalFileURL={finalFileURL}
                isFileArchived={isFileArchived}
                downloadProgress={downloadProgress}
              />
            </a>
            <div className="docInfo">
              <Metadata {...createMetadataProps(user)} />
            </div>
          </li>
        );
      } else if (
        ext === "ppt" ||
        ext === "pptx"
      ) {
        return (
          <li className="docs" key={listKey}>
            <CardBuilder {...createCardBuilderProps(user, 'nonVideo')} />
            {/* <a className="buttonPpt" href={finalFileURL} target="_blank"></a> */}
            <a
              className={`buttonPpt ${
                isFileArchived === "true" ? "archivedDoc" : ""
              }`}
              href={finalFileURL}
              onClick={createDownloadHandler(user, undefined)}
            >
              <CardDownloadIcon
                finalFileURL={finalFileURL}
                isFileArchived={isFileArchived}
                downloadProgress={downloadProgress}
              />
            </a>

            <div className="docInfo">
              <Metadata {...createMetadataProps(user)} />
            </div>
          </li>
        );
      } else if (ext === "pdf") {
        return (
          <li className="docs" key={listKey}>
            <CardBuilder {...createCardBuilderProps(user, 'nonVideo')} />
            {/* <a className="buttonPdf" href={finalFileURL} target="_blank"></a> */}
            <a
              className={`buttonPdf ${
                isFileArchived === "true" ? "archivedDoc" : ""
              }`}
              href={finalFileURL}
              onClick={createDownloadHandler(user, undefined)}
            >
              <CardDownloadIcon
                finalFileURL={finalFileURL}
                isFileArchived={isFileArchived}
                downloadProgress={downloadProgress}
              />
            </a>

            <div className="docInfo">
              <Metadata {...createMetadataProps(user)} />
            </div>
          </li>
        );
      } else {
        return (
          <li className="docs" key={listKey}>
            <CardBuilder {...createCardBuilderProps(user, 'nonVideo')} />
            {/* <a className="button" href={finalFileURL} target="_blank"></a> */}
            <a
              className={`button ${
                isFileArchived === "true" ? "archivedDoc" : ""
              }`}
              href={finalFileURL}
              onClick={createDownloadHandler(user, undefined)}
            >
              <CardDownloadIcon
                finalFileURL={finalFileURL}
                isFileArchived={isFileArchived}
                downloadProgress={downloadProgress}
              />
            </a>
            <div className="docInfo">
              <Metadata {...createMetadataProps(user)} />
            </div>
          </li>
        );
      }
    });
  }
 
  function openLightbox() {
    if (!blobUrls.length) return null;

    const safePhotoIndex =
      photoIndex >= 0 && photoIndex < blobUrls.length ? photoIndex : 0;

    return (
      <Lightbox
        animationOnKeyInput={true}
        clickOutsideToClose={false}
        imageTitle={`SA No. :\u00A0\u00A0${serviceAppointment[safePhotoIndex]}`}
        toolbarButtons={showHideMetaDataButton}
        imageCaption={
          isCaptionVisible
            ? `GPS Loc :\u00A0\u00A0${
                (lat[safePhotoIndex] == null ? "NA" : lat[safePhotoIndex]) +
                "\xa0\xa0\xa0\xa0\xa0" +
                (long[safePhotoIndex] == null ? "" : long[safePhotoIndex])
              }\u00A0;\u00A0
        CapturedDate :\u00A0\u00A0${
          capturedDate[safePhotoIndex] == null ? "NA" : capturedDate[safePhotoIndex]
        }\u00A0;\u00A0
        UploadedDate :\u00A0\u00A0${
          dateTime[safePhotoIndex] == null ? "NA" : dateTime[safePhotoIndex]
        }\u00A0;\u00A0
        Uploaded By :\u00A0\u00A0${
          uploadedBy[safePhotoIndex] == null ? "NA" : uploadedBy[safePhotoIndex]
        }\u00A0;\u00A0
        Organization :\u00A0\u00A0${
          organization[safePhotoIndex] == null ? "NA" : organization[safePhotoIndex]
        }\u00A0;\u00A0
        OCR Data :\u00A0\u00A0${ocrData[safePhotoIndex]}\u00A0\u00A0
        File Description :\u00A0\u00A0${
          fileDesc[safePhotoIndex] == null ? "NA" : fileDesc[safePhotoIndex]
        }\u00A0;\u00A0`
            : ``
        }
        mainSrc={blobUrls[safePhotoIndex]}
        mainSrcThumbnail={blobUrls[safePhotoIndex]}
        imagePadding={100}
        nextSrc={blobUrls[(safePhotoIndex + 1) % blobUrls.length]}
        prevSrc={blobUrls[(safePhotoIndex + blobUrls.length - 1) % blobUrls.length]}
        onCloseRequest={() => {
          setIsCaptionVisible(false);
          setIsOpenImage(false);
          return;
        }}
        onMovePrevRequest={handleMovePrevRequest}
        onMoveNextRequest={handleMoveNextRequest}
      />
    );
  }

  const PaginationUsers = ({
    extractedData,
    currentPage,
    setCurrentPage,
    isCheckAll,
  }) => {
    const maxButtons = 5; // Maximum number of buttons to display at once
    const halfMaxButtons = Math.floor(maxButtons / 2);

    // Calculate the range of pages to display
    const startPage = Math.max(1, currentPage - halfMaxButtons);
    const endPage = Math.min(extractedData.length, startPage + maxButtons - 1);

    const handleClick = (pageNumber) => () => {
      setCurrentPage(pageNumber);
      
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });      
    };
    return (
      <div className="pagination">
        <ul className="page">
          <li>
            <button
              className="color-button"
              onClick={handleClick(currentPage - 1)}
              disabled={currentPage === 1} // Disable if on the first page
            >
              Previous
            </button>
          </li>
          {Array.from(
            { length: endPage - startPage + 1 },
            (_, index) => startPage + index,
          ).map((pageNumber) => (
            <li
              key={pageNumber}
              className={currentPage === pageNumber ? "active" : ""}
            >
              <button
                className="color-button"
                onClick={handleClick(pageNumber)}
              >
                {pageNumber}
              </button>
            </li>
          ))}
          <li>
            <button
              className="color-button"
              onClick={handleClick(currentPage + 1)}
              disabled={currentPage === extractedData.length} // Disable if on the last page
            >
              Next
            </button>
          </li>
        </ul>
      </div>
    );
  };
  
  const pageSize = 250;
  let totalCount = 0;
  let extractedData = [];
  let tempGroup = [];

  const flushPage = () => {
    if (tempGroup.length === 0) return;
    extractedData.push({
      data: tempGroup,
      ArrayLength: totalCount,
    });
    tempGroup = [];
    totalCount = 0;
  };

  const getOrCreateWoli = (workOrderLineItemNumber) => {
    let woliGroup = tempGroup.find(
      (item) => item.workOrderLineItemNumber === workOrderLineItemNumber,
    );
    if (!woliGroup) {
      woliGroup = {
        workOrderLineItemNumber,
        data: [],
      };
      tempGroup.push(woliGroup);
    }
    return woliGroup;
  };

  // console.log("Users data before pagination", users);
  visibleUsers.forEach((user) => {
    const currentWorkOrderLineItemNumber = user.workOrderLineItemNumber;

    user.data.forEach((subItem) => {
      const filesWithNumbers = subItem.data.map((item, index) => ({
        ...item,
        FileNo: index + 1,
      }));

      let startIndex = 0;
      while (startIndex < filesWithNumbers.length) {
        const availableFileSlots = pageSize - totalCount;
        if (availableFileSlots <= 0) {
          flushPage();
          continue;
        }

        const amountToAdd = Math.min(
          availableFileSlots,
          filesWithNumbers.length - startIndex,
        );
        const chunk = filesWithNumbers.slice(
          startIndex,
          startIndex + amountToAdd,
        );

        const targetWoli = getOrCreateWoli(currentWorkOrderLineItemNumber);
        let targetSa = targetWoli.data.find(
          (sa) => sa.serviceAppointmentNumber === subItem.serviceAppointmentNumber,
        );

        if (!targetSa) {
          targetSa = {
            ...subItem,
            data: [],
          };
          targetWoli.data.push(targetSa);
        }

        targetSa.data = [...targetSa.data, ...chunk];
        totalCount += amountToAdd;
        startIndex += amountToAdd;

        if (totalCount >= pageSize) {
          flushPage();
        }
      }
    });
  });

  flushPage();

  //Removing Woli with No data to prevent blank page.
  extractedData = extractedData.filter((item) => item.data.length > 0);
  const currentItemsUsers = extractedData.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const hasVisibleItems = currentItemsUsers.length > 0;

  const getCurrentPageCollapseData = () => {
    const woliEntries = [];
    const saEntries = [];

    currentItemsUsers.forEach((page, pageIndex) => {
      page.data.forEach((woli, woliIndex) => {
        const woliKey = `${currentPage}-${pageIndex}-${woliIndex}-${woli.workOrderLineItemNumber}`;
        const hasWoliPreference = Object.prototype.hasOwnProperty.call(
          collapsedWolis,
          woliKey,
        );
        const isWoliCollapsed = hasWoliPreference
          ? Boolean(collapsedWolis[woliKey])
          : page.data.length > 1;

        woliEntries.push({ woliKey, isWoliCollapsed });

        woli.data.forEach((sa, saIndex) => {
          const saKey = `${woliKey}-${saIndex}-${sa.serviceAppointmentNumber}`;
          const hasSaPreference = Object.prototype.hasOwnProperty.call(
            collapsedSas,
            saKey,
          );
          const isSaCollapsed = hasSaPreference
            ? Boolean(collapsedSas[saKey])
            : woli.data.length > 1;

          saEntries.push({ saKey, isSaCollapsed });
        });
      });
    });

    const hasRows = woliEntries.length > 0;
    const allExpanded =
      hasRows &&
      woliEntries.every((entry) => !entry.isWoliCollapsed) &&
      saEntries.every((entry) => !entry.isSaCollapsed);

    return { woliEntries, saEntries, allExpanded };
  };

  const { allExpanded: isAllExpanded } = getCurrentPageCollapseData();

  const handleToggleAllCollapse = () => {
    const { woliEntries, saEntries, allExpanded } = getCurrentPageCollapseData();
    const nextIsCollapsed = allExpanded;

    setCollapsedWolis((prev) => {
      const updated = { ...prev };
      woliEntries.forEach(({ woliKey }) => {
        updated[woliKey] = nextIsCollapsed;
      });
      return updated;
    });

    setCollapsedSas((prev) => {
      const updated = { ...prev };
      saEntries.forEach(({ saKey }) => {
        updated[saKey] = nextIsCollapsed;
      });
      return updated;
    });
  };
  const handleSingleDateModeChange = (checked) => {
    setIsSingleDateMode(checked);

    if (checked) {
      setCapturedDateTo("");
    }
  };

  //Array of total id's (250) of sliced data to use Select All button.
  // currentItemsUsers.forEach((item) => {
  //   arrayWoli = arrayWoli.concat(
  //     item.data.flatMap((innerItem) => innerItem.data),
  //   );
  // });

 return (
          <>
            <ErrorDisplay
              errorMessage={errorMessage}
              unavailable={unavailable}
              onClose={onClose}
            />
            {errorMessage === "" && unavailable !== true && (
              <section>
                <div className="imageGrid">
                  <div className="headerContainer">
                    <div className="headerRow">
                      <div className="headerLeft">
                        <h4>Work Order: {workdIds[0]}</h4>
                      </div>
                      <div className="headerTop">
                        {archivedImages.some(
                          (img) => img.isFileArchived === "true",
                        ) && (
                          <button
                            className="btn-download btn-reArchive"
                            onClick={() =>
                              handleReArchive(
                                loaderdiv,
                                loaderOverlaydiv,
                                axios,
                                accessToken,
                                createDialogBox,
                                closeDialogBox,
                                downloadImages,
                                listThumbnails,
                                setDownloadImages,
                                setArchivedImages,
                                setListThumbnails,
                                setIsCheck,
                                setIsCheckAll,
                              )
                            }
                          >
                            Restore from archive
                          </button>
                        )}
                      </div>

                      <div className="headerActions">
                        <button
                          className="btn-download btn-expand-collapse"
                          onClick={handleToggleAllCollapse}
                        >
                          <span className="expandCollapseIcon" aria-hidden="true">
                            {isAllExpanded ? (
                              <svg viewBox="0 0 24 24" role="img" focusable="false">
                                <path
                                  d="M6 9l6 6 6-6"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" role="img" focusable="false">
                                <path
                                  d="M9 6l6 6-6 6"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </span>
                          <span>{isAllExpanded ? "Collapse All" : "Expand All"}</span>
                        </button>

                        <SelectAllCheckbox
                          isChecked={isCheckAll}
                          onChange={handleSelectAll}
                        />

                        <DownloadButton
                          wo={workdIds[0]}
                          ImgUri={ImgUri}
                          setDownloadProgress={setDownloadProgress}
                          downloadProgress={downloadProgress}
                          setArchivedImages={setArchivedImages}
                          downloadImages={downloadImages}
                          setDownloadImages={setDownloadImages}
                          setIsCheck={setIsCheck}
                          setIsCheckAll={setIsCheckAll}
                          isCheckAll={isCheckAll}
                          isCheck={isCheck}
                          setIsDownloadSidebarVisible={setIsDownloadSidebarVisible}
                          videoSasToken={videoSasToken}
                          nonVideoSasToken={nonVideoSasToken}
                          createDialogBox={createDialogBox}
                          closeDialogBox={closeDialogBox}
                          fetchVideoSasToken={fetchVideoSasToken}
                          fetchNonVideoSasToken={fetchNonVideoSasToken}
                        />

                        <button
                          type="button"
                          className={`btn-download btn-gallery-controls ${isGalleryControlActive ? "is-active" : ""}`}
                          onClick={() => setIsGalleryControlsOpen((prev) => !prev)}
                          aria-pressed={isGalleryControlsOpen}
                          title="Sort and filter"
                          aria-label="Open gallery sort and filter controls"
                        >
                          <span className="galleryControlsIcon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" role="img" focusable="false">
                              <path
                                d="M3 5h18l-7 8v5l-4 1v-6L3 5z"
                                fill="currentColor"
                              />
                            </svg>
                          </span>
                        </button>

                        {/* Code for Exporting metadata into excel */}
                        {/* <DownloadExcelButton
                             currentItemsUsers={currentItemsUsers}
                            /> */}
                      </div>
                    </div>
                  </div>
                  <div className="woliLayout">
                    <div className="woliContent">
                      <div className="woli">
                        {hasVisibleItems ? (
                          <>
                            {currentItemsUsers.map((page, pageIndex) =>
                              page.data.map((woli, woliIndex) => {
                                const woliKey = `${currentPage}-${pageIndex}-${woliIndex}-${woli.workOrderLineItemNumber}`;
                                const hasWoliPreference = Object.prototype.hasOwnProperty.call(
                                  collapsedWolis,
                                  woliKey,
                                );
                                const isWoliCollapsed = hasWoliPreference
                                  ? Boolean(collapsedWolis[woliKey])
                                  : page.data.length > 1;

                                return (
                                  <div key={woliKey}>
                                    <h5
                                      className="workLine"
                                      onClick={() =>
                                        toggleWoliCollapse(
                                          woliKey,
                                          woli,
                                          isWoliCollapsed,
                                        )
                                      }
                                    >
                                      <span className="collapseBannerIcon" aria-hidden="true">
                                        {isWoliCollapsed ? (
                                          <svg viewBox="0 0 24 24" role="img" focusable="false">
                                            <path
                                              d="M9 6l6 6-6 6"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        ) : (
                                          <svg viewBox="0 0 24 24" role="img" focusable="false">
                                            <path
                                              d="M6 9l6 6 6-6"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2.5"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            />
                                          </svg>
                                        )}
                                      </span>
                                      <span className="collapseBannerLabel">WOLI</span>
                                      <span className="collapseBannerValue">
                                        {woli.workOrderLineItemNumber}
                                      </span>
                                    </h5>

                                    {!isWoliCollapsed &&
                                      woli.data.map((sa, saIndex) => {
                                        const saKey = `${woliKey}-${saIndex}-${sa.serviceAppointmentNumber}`;
                                        const hasSaPreference = Object.prototype.hasOwnProperty.call(
                                          collapsedSas,
                                          saKey,
                                        );
                                        const isSaCollapsed = hasSaPreference
                                          ? Boolean(collapsedSas[saKey])
                                          : woli.data.length > 1;

                                        return (
                                          <div key={saKey}>
                                            <h6
                                              className="sa"
                                              onClick={() =>
                                                toggleSaCollapse(saKey, isSaCollapsed)
                                              }
                                            >
                                              <span className="collapseBannerIcon" aria-hidden="true">
                                                {isSaCollapsed ? (
                                                  <svg viewBox="0 0 24 24" role="img" focusable="false">
                                                    <path
                                                      d="M9 6l6 6-6 6"
                                                      fill="none"
                                                      stroke="currentColor"
                                                      strokeWidth="2.5"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                    />
                                                  </svg>
                                                ) : (
                                                  <svg viewBox="0 0 24 24" role="img" focusable="false">
                                                    <path
                                                      d="M6 9l6 6 6-6"
                                                      fill="none"
                                                      stroke="currentColor"
                                                      strokeWidth="2.5"
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                    />
                                                  </svg>
                                                )}
                                              </span>
                                              <span className="collapseBannerLabel">SA No.</span>
                                              <span className="collapseBannerValue">
                                                {sa.serviceAppointmentNumber}
                                              </span>
                                            </h6>
                                            {!isSaCollapsed && (
                                              <div className="sa-flex-row">
                                                <ol type="1" className="users">
                                                  {renderImageListItems(
                                                    sa.data,
                                                    downloadProgress,
                                                    handleDownloadClick,
                                                  )}
                                                </ol>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>
                                );
                              }),
                            )}
                            {isOpenImage && openLightbox()}
                            <PaginationUsers
                              extractedData={extractedData}
                              currentPage={currentPage}
                              setCurrentPage={setCurrentPage}
                              isCheckAll={setIsCheckAll}
                            />
                          </>
                        ) : (
                          <div className="galleryEmptyState" role="status" aria-live="polite">
                            <h3>No Items Found</h3>
                            <p>
                              Your current sort or filter settings returned no gallery items. Clear the filters to show everything again.
                            </p>
                            <button
                              type="button"
                              className="galleryEmptyStateButton"
                              onClick={handleClearGalleryFilters}
                            >
                              Clear filters
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {isGalleryControlsOpen && (
                      <aside className="galleryControlsPane" aria-label="Gallery controls">
                        <div className="galleryControlsCard">
                          <div className="galleryControlsHeader">
                            <div>
                              <p className="galleryControlsTitle">Gallery controls</p>
                              <p className="galleryControlsSubtitle">
                                {galleryControlsTab === "sort"
                                  ? "Choose how the images are ordered."
                                  : "Narrow the images shown in the gallery."}
                              </p>
                            </div>
                            <button
                              type="button"
                              className="galleryControlsCloseButton"
                              onClick={() => setIsGalleryControlsOpen(false)}
                            >
                              Close
                            </button>
                          </div>

                          <div className="galleryControlsTabs" role="tablist" aria-label="Gallery controls tabs">
                            <button
                              type="button"
                              className={`galleryControlsTab ${galleryControlsTab === "sort" ? "active" : ""}`}
                              onClick={() => setGalleryControlsTab("sort")}
                              role="tab"
                              aria-selected={galleryControlsTab === "sort"}
                            >
                              Sort
                            </button>
                            <button
                              type="button"
                              className={`galleryControlsTab ${galleryControlsTab === "filter" ? "active" : ""}`}
                              onClick={() => setGalleryControlsTab("filter")}
                              role="tab"
                              aria-selected={galleryControlsTab === "filter"}
                            >
                              Filter
                            </button>
                          </div>

                          <div className="galleryControlsBody">
                            {galleryControlsTab === "sort" ? (
                              <div className="galleryControlsSection">
                                <div className="galleryControlsSectionHeader">
                                  <h5>Sort</h5>
                                  <button
                                    type="button"
                                    className="galleryControlsTextButton"
                                    onClick={() => setSortMode("captured-desc")}
                                  >
                                    Reset
                                  </button>
                                </div>

                                <div className="gallerySortList">
                                  {[
                                    { value: "captured-desc", label: "Sort new to oldest (Captured Date)" },
                                    { value: "captured-asc", label: "Sort oldest to new (Captured Date)" },
                                    { value: "engineer-asc", label: "Sort by Engineer" },
                                  ].map((option) => (
                                    <label key={option.value} className="gallerySortOption">
                                      <input
                                        type="radio"
                                        name="gallerySortMode"
                                        value={option.value}
                                        checked={sortMode === option.value}
                                        onChange={() => setSortMode(option.value)}
                                      />
                                      <span>{option.label}</span>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="galleryControlsSection">
                                <div className="galleryControlsSectionHeader">
                                  <h5>Filter</h5>
                                  <button
                                    type="button"
                                    className="galleryControlsTextButton"
                                    onClick={handleClearGalleryFilters}
                                  >
                                    Clear all
                                  </button>
                                </div>

                                <div className="galleryFilterGroup">
                                  <h6>File Type</h6>
                                  <div className="galleryFilterGrid">
                                    {fileTypeOptions.map((option) => (
                                      <label key={option.key} className="galleryCheckboxOption">
                                        <input
                                          type="checkbox"
                                          checked={selectedFileTypes[option.key]}
                                          onChange={() =>
                                            setSelectedFileTypes((prev) => ({
                                              ...prev,
                                              [option.key]: !prev[option.key],
                                            }))
                                          }
                                        />
                                        <span>{option.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>

                                <div className="galleryFilterGroup">
                                  <h6>Date Captured</h6>
                                  <div className="galleryDateRow">
                                    <label className="galleryDateField">
                                      <span>From</span>
                                      <input
                                        type="date"
                                        value={capturedDateFrom}
                                        onChange={(e) => setCapturedDateFrom(e.target.value)}
                                      />
                                    </label>
                                    {!isSingleDateMode && <label className="galleryDateField">
                                      <span>To</span>
                                      <input
                                        type="date"
                                        value={capturedDateTo}
                                        onChange={(e) => setCapturedDateTo(e.target.value)}
                                      />
                                    </label>}
                                  </div>
                                   <label className="galleryCheckboxOption">
                                      <input
                                        type="checkbox"
                                        checked={isSingleDateMode}
                                        onChange={(e) =>
                                          handleSingleDateModeChange(e.target.checked)
                                        }
                                      />
                                      Select Single Date
                                    </label>
                                  <label className="galleryCheckboxOption galleryCheckboxOptionSingle">
                                    <input
                                      type="checkbox"
                                      checked={includeNoCapturedDate}
                                      onChange={(e) =>
                                        setIncludeNoCapturedDate(e.target.checked)
                                      }
                                    />
                                    <span>Include files with no Captured Date</span>
                                  </label>
                                </div>

                                <div className="galleryFilterGroup">
                                  <h6>Engineer</h6>
                                  <input
                                    type="search"
                                    className="gallerySearchInput"
                                    placeholder="Search engineer names"
                                    value={engineerSearch}
                                    onChange={(e) => setEngineerSearch(e.target.value)}
                                  />
                                  <div className="galleryEngineerList">
                                    {engineerOptions
                                      .filter((name) =>
                                        name.toLowerCase().includes(engineerSearch.trim().toLowerCase()),
                                      )
                                      .map((name) => (
                                        <label key={name} className="galleryCheckboxOption">
                                          <input
                                            type="checkbox"
                                            checked={!excludedEngineers.includes(name)}
                                            onChange={() => toggleEngineerSelection(name)}
                                          />
                                          <span>{name}</span>
                                        </label>
                                      ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </aside>
                    )}
                    {isDownloadSidebarVisible && (
                      <div className="downloadSidebarPane">
                        <DownloadSidebar
                          downloadProgress={downloadProgress}
                          setDownloadProgress={setDownloadProgress}
                          downloadImages={downloadImages}
                          isCollapsed={isDownloadSidebarCollapsed}
                          setIsCollapsed={setIsDownloadSidebarCollapsed}
                          setDownloadImages={setDownloadImages}
                          setIsCheck={setIsCheck}
                          setIsCheckAll={setIsCheckAll}
                          setIsDownloadSidebarVisible={
                            setIsDownloadSidebarVisible
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}
          </>
        );
};
export default CCTV_Gallery;
